'use client';

import { useEffect } from 'react';

const FLAG_KEY = 'la_internal';

/**
 * Client-side internal-traffic guard. Visit any page with `?internal=1` (or
 * `?internal=0` to clear) to flip a localStorage flag on this browser. While
 * the flag is set, every `window.gtag` call becomes a no-op so the developer's
 * own browsing never reaches GA4.
 *
 * Belt-and-braces: GA4's own internal-traffic filter (IP-based) is the
 * authoritative gate, but that breaks when the developer is on mobile / a
 * different network. This flag survives across networks.
 */
export default function InternalTrafficGuard() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    const param = url.searchParams.get('internal');
    if (param === '1') {
      localStorage.setItem(FLAG_KEY, '1');
      console.log('[InternalTrafficGuard] enabled — gtag is now a no-op on this browser');
    } else if (param === '0') {
      localStorage.removeItem(FLAG_KEY);
      console.log('[InternalTrafficGuard] disabled — gtag restored');
    }

    // Headless/automation browsers (navigator.webdriver) are bot traffic —
    // e.g. the "China + English, zero engagement" scraper fleet. GA4 data
    // filters can't exclude them, so silence gtag client-side.
    const isBot = navigator.webdriver === true;

    if (isBot || localStorage.getItem(FLAG_KEY) === '1') {
      // Override gtag with a no-op. Survives across the GA4 script loading
      // (which assigns to window.gtag asynchronously) by re-overriding via a
      // getter/setter pair so any later assignment is intercepted.
      const noop = () => {};
      try {
        Object.defineProperty(window, 'gtag', {
          configurable: true,
          get() { return noop; },
          set() { /* swallow re-assignment */ },
        });
      } catch {
        (window as { gtag?: unknown }).gtag = noop;
      }
    }
  }, []);

  return null;
}
